import { useEffect } from 'react';
import { focusFirstErrorField } from 'Helpers/metaForm';

const hasErrors = (errors) => errors && errors.length > 0;

export function useMetaFieldsImmediatelyRequired({
  fieldsImmediatelyRequired, errors, resource, setErrors,
}) {
  useEffect(() => {
    let canceled = false; // If the dependencies change and this useEffect's cleanup (cancel) is run, this will become true
    const cancel = () => { canceled = true; };

    // If fields should be immediately considered required, and the server hasn't given more explicit errors, we need to forge up some errors for the required fields
    if (!fieldsImmediatelyRequired || hasErrors(errors)) {
      return cancel;
    }

    setErrors((prevState) => {
      // In case errors has been mutated asynchronously or the dependencies have changed causing cancel to be true, let those errors stay
      if (hasErrors(prevState) || canceled) {
        return prevState;
      }

      // Compute the new errors, but don't set them just yet
      const newErrors = resource
        .meta()
        .template()
        .list()
        .filter(field => field.attr('required') && !field.attr('readonly') && !resource.attr(field.attr('name')))
        .map(field => ({ source: field.attr('name'), fakeError: true }));

      // If this function computes that there are no errors, it will be returning an empty array, and that empty array will be in a different place in memory than the existing empty array.
      // That will trigger this same useEffect because this thing depends on errors, and since errors is an empty array and this code changes errors if it's an empty array, this same code will run again.
      // In other words, we'd have an infinite loop.
      // The following if statement is critical to avoid that infinite loop - if we get to here, we know that prevState has no errors and we also have found no errors to add, so let's use whatever was in prevState so that this useEffect doesn't trigger itself
      if (!hasErrors(newErrors)) {
        return prevState;
      }

      // We've avoided the infinite loop, now we know we can return the errors we've computed.
      // But first, let's focus the error
      focusFirstErrorField(resource, newErrors);
      // Now we can return the errors
      return newErrors;
    });

    return cancel;
  }, [fieldsImmediatelyRequired, errors, resource.instanceId()]);
}
